'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import ShareButtons from '@/app/components/ShareButtons';
import { getTotalPrice, getFormattedTotal, getPerCardAverage, getMarginalCost, applyPromoToTotal, type PromoCode } from '@/app/lib/pricing';

const MAX_CHARS = Number(process.env.NEXT_PUBLIC_MAX_CHARS) || 270;

const repSlug = (first: string, last: string) =>
  `${first}-${last}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const getRepId = (rep: any): string => rep?.references?.bioguide_id || rep?.id || '';
const getRepFullName = (rep: any): string =>
  rep?.name || `${rep?.bio?.first_name || ''} ${rep?.bio?.last_name || ''}`.trim() || 'Unknown';
const getRepTitle = (rep: any): string => {
  if (rep?.level === 'state') {
    if (rep?.jurisdiction === 'dc') return 'Council Member';
    return rep?.chamber === 'upper' ? 'State Senator' : 'State Representative';
  }
  return rep?.type === 'senator' ? 'U.S. Senator' : 'U.S. Representative';
};
const getRepState = (rep: any): string =>
  (rep?.jurisdiction || rep?.bio?.state || '').toUpperCase();
const getRepParty = (rep: any): string => rep?.party || rep?.bio?.party || 'Unknown';

const US_STATES = [
  ['al','Alabama'],['ak','Alaska'],['az','Arizona'],['ar','Arkansas'],['ca','California'],
  ['co','Colorado'],['ct','Connecticut'],['de','Delaware'],['fl','Florida'],['ga','Georgia'],
  ['hi','Hawaii'],['id','Idaho'],['il','Illinois'],['in','Indiana'],['ia','Iowa'],
  ['ks','Kansas'],['ky','Kentucky'],['la','Louisiana'],['me','Maine'],['md','Maryland'],
  ['ma','Massachusetts'],['mi','Michigan'],['mn','Minnesota'],['ms','Mississippi'],['mo','Missouri'],
  ['mt','Montana'],['ne','Nebraska'],['nv','Nevada'],['nh','New Hampshire'],['nj','New Jersey'],
  ['nm','New Mexico'],['ny','New York'],['nc','North Carolina'],['nd','North Dakota'],['oh','Ohio'],
  ['ok','Oklahoma'],['or','Oregon'],['pa','Pennsylvania'],['ri','Rhode Island'],['sc','South Carolina'],
  ['sd','South Dakota'],['tn','Tennessee'],['tx','Texas'],['ut','Utah'],['vt','Vermont'],
  ['va','Virginia'],['wa','Washington'],['wv','West Virginia'],['wi','Wisconsin'],['wy','Wyoming'],
  ['dc','Washington D.C.'],['pr','Puerto Rico'],
] as const;

const DEFAULT_EXAMPLE_MESSAGES = [
  "Please fix the roads in our district. The current potholes on Main Street are causing severe damage to local vehicles and slowing down emergency response times.",
  "I am urging you to vote NO on the upcoming tax hike. Working families in our district are already struggling with inflation and cannot afford additional financial burdens right now.",
  "I am writing to urge you to support the clean energy bill. Investing in renewable infrastructure will create local jobs and ensure a sustainable future for our children.",
  "Please protect our local national parks from commercial development. Keeping public lands in public hands is essential for our local tourism economy and wildlife preservation."
];

type StepStr = 'search' | 'select' | 'write';

interface SearchFlowProps {
  showBadge?: boolean;
  showHowItWorks?: boolean;
  exampleMessages?: string[];
  abroad?: boolean;
}

export default function SearchFlow({
  showBadge = false,
  showHowItWorks = false,
  exampleMessages = DEFAULT_EXAMPLE_MESSAGES,
  abroad = false,
}: SearchFlowProps) {
  const [step, setStep] = useState<StepStr>('search');
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState<'address' | 'name'>('address');
  const [address, setAddress] = useState('');
  const [repName, setRepName] = useState('');

  const [cart, setCart] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [geocodedAddress, setGeocodedAddress] = useState<{ line1: string; city: string; state: string; zip: string } | null>(null);
  const [votingAddressInput, setVotingAddressInput] = useState({ line1: '', city: '', state: '', zip: '' });

  const [message, setMessage] = useState('');
  const [matchedCampaign, setMatchedCampaign] = useState<{ id: string; slug: string | null; send_count: number } | null>(null);
  const [campaignCopied, setCampaignCopied] = useState(false);
  const matchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{promo: PromoCode, label: string} | null>(null);
  const upsellShownAtRef = useRef<number | null>(null);
  const [promoError, setPromoError] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [okToContact, setOkToContact] = useState(true);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [govLevels, setGovLevels] = useState<Set<'federal' | 'state'>>(new Set(['federal']));
  const [stateJurisdiction, setStateJurisdiction] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searchNotice, setSearchNotice] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [totalSent, setTotalSent] = useState<number | null>(null);

  // ==========================================
  // BROWSER BACK BUTTON SUPPORT
  // ==========================================
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    const nameParam = params.get('name');
    const urlPromo = params.get('promo');
    const levelsParam = params.get('levels');
    if (levelsParam) {
      const ls = levelsParam.split(',').filter((l): l is 'federal' | 'state' =>
        l === 'federal' || l === 'state'
      );
      if (ls.length > 0) setGovLevels(new Set(ls));
    }

    if (nameParam) {
      setSearchMode('name');
      setRepName(nameParam);
      setLoading(true);
      fetch('/api/reps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nameParam, levels: ['federal'] }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.federalLegislators?.length) {
            setSearchResults(data.federalLegislators);
            setCart(data.federalLegislators);
            setStep('select');
            window.history.replaceState({ step: 'select' }, '', '?mode=name&levels=federal#select');
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      if (modeParam === 'name') setSearchMode('name');
      window.history.replaceState(
        { step: 'search' }, '',
        modeParam === 'name' ? '?mode=name#search' : '?mode=address#search'
      );
    }

    if (urlPromo) {
      setPromoCode(urlPromo);
      setShowPromoInput(true);
      fetch('/api/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: urlPromo, quantity: 1 }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.valid) {
            setAppliedPromo({ promo: data.promo, label: data.label });
          }
        })
        .catch(() => {});
    }

    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.step) {
        setStep(event.state.step as StepStr);
      } else {
        setStep('search');
      }
    };

    window.addEventListener('popstate', handlePopState);

    if (abroad) window.umami?.track('abroad_flow_started');

    if (!abroad) {
      fetch('/api/stats')
        .then(r => r.json())
        .then(data => {
          if (typeof data.totalSent === 'number') setTotalSent(data.totalSent);
        })
        .catch(() => {});
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, [abroad]);

  // Debounced exact-match campaign detection
  useEffect(() => {
    if (matchDebounceRef.current) clearTimeout(matchDebounceRef.current);
    if (!message.trim()) { setMatchedCampaign(null); return; }
    matchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/messages/search?exact=true&q=${encodeURIComponent(message)}`);
        const data = await res.json();
        setMatchedCampaign(data[0] ?? null);
      } catch { /* ignore */ }
    }, 400);
  }, [message]);

  const handleCampaignCopy = async () => {
    const url = `https://postcardstocongress.org/m/${matchedCampaign!.slug || matchedCampaign!.id}`;
    await navigator.clipboard.writeText(url);
    setCampaignCopied(true);
    setTimeout(() => setCampaignCopied(false), 2000);
    if (typeof window.umami !== 'undefined') window.umami.track('campaign_link_copied');
  };

  const goToStep = (newStep: StepStr) => {
    setStep(newStep);
    const mode = searchMode === 'name' ? 'name' : 'address';
    const lvls = Array.from(govLevels).join(',');
    window.history.pushState({ step: newStep }, '', `?mode=${mode}&levels=${lvls}#${newStep}`);
  };

  // --- TYPEWRITER EFFECT STATE ---
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [displayedExampleText, setDisplayedExampleText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    if (step !== 'search') return;
    const fullText = exampleMessages[currentExampleIndex];
    if (isTyping) {
      if (displayedExampleText.length < fullText.length) {
        const timeout = setTimeout(() => {
          setDisplayedExampleText(fullText.slice(0, displayedExampleText.length + 1));
        }, 50);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => setIsTyping(false), 3500);
        return () => clearTimeout(timeout);
      }
    } else {
      setDisplayedExampleText('');
      setCurrentExampleIndex((prev) => (prev + 1) % exampleMessages.length);
      setIsTyping(true);
    }
  }, [displayedExampleText, isTyping, currentExampleIndex, step, exampleMessages]);

  // ==========================================
  // SEARCH
  // ==========================================
  const runSearch = async () => {
    setLoading(true);
    setSearchError('');
    setSearchNotice('');
    try {
      const levels = Array.from(govLevels);
      let payload: Record<string, unknown>;
      if (searchMode === 'name') {
        if (govLevels.has('state') && !stateJurisdiction) {
          setSearchError('Please select a state to search state legislators by name.');
          return null;
        }
        payload = { name: repName, levels, ...(govLevels.has('state') ? { jurisdiction: stateJurisdiction } : {}) };
      } else {
        payload = { address, levels };
      }

      const response = await fetch('/api/reps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        if (data.stateNotice) setSearchNotice(data.stateNotice);
        if (data.geocodedAddress) setGeocodedAddress(data.geocodedAddress);
        const allResults = [
          ...(data.federalLegislators || []),
          ...(data.stateLegislators || []),
        ];
        setSearchResults(allResults);
        if (typeof window.umami !== 'undefined') {
          if (data.stateLegislators?.length > 0) {
            window.umami.track('state_lookup_completed', {
              jurisdiction: stateJurisdiction || 'geo',
              result_count: data.stateLegislators.length,
            });
          }
        }
        if (allResults.length === 0) {
          setSearchError(searchMode === 'name'
            ? 'No representatives found by that name. Check the spelling and try again.'
            : 'No representatives found for that address. Try including your city and ZIP code.');
        }
        return allResults;
      } else {
        setSearchError(data.error || 'Something went wrong searching for reps. Please try again.');
        return null;
      }
    } catch (error) {
      console.error(error);
      setSearchError('Could not reach the server. Check your connection and try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const legislators = await runSearch();
    if (legislators) {
      addAllToCart(legislators);
      goToStep('select');
    }
  };

  const handleSearchAgain = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSearch();
  };

  // ==========================================
  // CART OPERATIONS
  // ==========================================
  const cartIds = cart.map((r: any) => getRepId(r));
  const hasUnadded = searchResults.some((r: any) => !cartIds.includes(getRepId(r)));

  const addToCart = (rep: any) => {
    const id = getRepId(rep);
    if (!id || cartIds.includes(id)) return;
    const prevSize = cart.length;
    const newSize = prevSize + 1;
    if (upsellShownAtRef.current && Date.now() - upsellShownAtRef.current < 30000) {
      window.umami?.track('upsell_prompt_converted', { from_count: prevSize, to_count: newSize });
    }
    if ([3, 5, 8].includes(newSize)) {
      window.umami?.track('cart_tier_reached', { card_count: newSize, total_cents: getTotalPrice(newSize) });
    }
    setCart(prev => [...prev, rep]);
  };

  const addAllToCart = (legislators: any[]) => {
    setCart(prev => {
      const existingIds = prev.map((r: any) => getRepId(r));
      const toAdd = legislators.filter((r: any) => getRepId(r) && !existingIds.includes(getRepId(r)));
      return [...prev, ...toAdd];
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter((r: any) => getRepId(r) !== id));
  };

  // ==========================================
  // MESSAGE + CHECKOUT
  // ==========================================
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARS) setMessage(text);
  };

  const handleCheckout = async () => {
    setCheckoutError('');
    if (abroad) {
      const { line1, city, state, zip } = votingAddressInput;
      if (!line1.trim() || !city.trim() || !state.trim() || !zip.trim()) {
        setCheckoutError('Please complete your U.S. voting address to continue.');
        return;
      }
    }

    setIsCheckingOut(true);

    const selectedRepIds = cart.map((r: any) => getRepId(r));
    const repNames = cart.map((rep: any) => {
      const title = getRepTitle(rep).replace('U.S. ', '');
      return `${title} ${getRepFullName(rep)}`.trim();
    });
    const repTypes = cart.map((rep: any) => {
      if (rep?.level === 'state') return '';
      return rep?.type === 'senator' ? 'S' : 'H';
    });
    const repAddresses = cart.map((rep: any) => {
      if (rep?.level === 'state') return rep.capitol_address ?? null;
      return null;
    });

    if (typeof window.umami !== 'undefined') {
      const stateReps = cart.filter((r: any) => r?.level === 'state');
      stateReps.forEach((r: any) => {
        window.umami?.track('state_postcard_sent', { jurisdiction: r.jurisdiction, chamber: r.chamber });
      });
    }

    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, selectedRepIds, repNames, repTypes, repAddresses, address, promoCode: appliedPromo ? promoCode : '', okToContact, subscribeNewsletter, ...(abroad ? { abroad: true, votingAddress: votingAddressInput } : {}) }),
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError(data.error || 'Checkout failed. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setCheckoutError('A network error occurred during checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    setAppliedPromo(null);
    try {
      const res = await fetch('/api/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, quantity: cart.length }),
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedPromo({ promo: data.promo, label: data.label });
      } else {
        setPromoError(data.error || 'Invalid promo code');
      }
    } catch {
      setPromoError('Could not validate code. Try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  const cartTotal = (qty: number) => appliedPromo
    ? applyPromoToTotal(getTotalPrice(qty), appliedPromo.promo, qty)
    : getTotalPrice(qty);

  useEffect(() => {
    if (abroad && geocodedAddress) setVotingAddressInput(geocodedAddress);
  }, [abroad, geocodedAddress]);

  useEffect(() => {
    if (step !== 'select' || cart.length < 1 || cart.length >= 8) return;
    window.umami?.track('upsell_prompt_shown', {
      current_count: cart.length,
      next_tier_count: cart.length + 1,
      marginal_cost_cents: getMarginalCost(cart.length),
    });
    upsellShownAtRef.current = Date.now();
  }, [cart.length, step]);

  const previewRepData = cart[0];
  const previewRepName = previewRepData ? getRepFullName(previewRepData) : 'Representative';
  const previewAddressLines: string[] = (() => {
    if (!previewRepData) return ['U.S. House of Representatives', 'Washington, DC 20515'];
    if (previewRepData.level === 'state') {
      const ca = previewRepData.capitol_address;
      if (ca?.line1) return [ca.line1, `${ca.city}, ${ca.state} ${ca.zip}`];
      return ['State Capitol', ''];
    }
    return previewRepData.type === 'senator'
      ? ['United States Senate', 'Washington, DC 20510']
      : ['U.S. House of Representatives', 'Washington, DC 20515'];
  })();

  return (
    <main className={`relative flex flex-col items-center p-4 sm:p-6 font-sans selection:bg-red-500/30 selection:text-white ${abroad ? '' : 'min-h-screen overflow-hidden'}`}>

      {/* --- AMBIENT AMERICANA BACKGROUND --- */}
      {!abroad && (
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
          <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-blue-900/25 blur-[120px]"></div>
          <div className="absolute top-[10%] -right-[10%] w-[50%] h-[50%] rounded-full bg-amber-600/10 blur-[120px]"></div>
          <div className="absolute -bottom-[20%] left-[20%] w-[60%] h-[60%] rounded-full bg-amber-900/10 blur-[100px]"></div>
        </div>
      )}

      {/* ================= STEP 1: SEARCH ================= */}
      {step === 'search' && (
        <div className={`max-w-4xl w-full text-center space-y-6 sm:space-y-8 animate-in fade-in zoom-in-95 duration-500 z-10 flex flex-col items-center ${abroad ? 'mt-0' : 'mt-12 sm:mt-20'}`}>

          {showBadge && (
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 text-amber-400 text-sm font-medium">
              🇺🇸 Make Your Voice Heard
            </div>
          )}

          {!abroad && (
            <>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-white leading-tight drop-shadow-sm">
                Emails get deleted.<br/>
                <span className="bg-gradient-to-r from-red-500 to-amber-400 bg-clip-text text-transparent">Postcards get read.</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto px-4 font-medium">
                Send a physical, printed postcard directly to your representatives&apos; desks in 60 seconds.
              </p>
              {totalSent !== null && totalSent >= 100 && (
                <p className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 text-amber-300 text-sm font-semibold animate-in fade-in duration-700">
                  📬 {totalSent.toLocaleString()} postcards sent to Congress so far
                </p>
              )}
            </>
          )}

          <div className="bg-[#fafaf7] p-6 sm:p-8 rounded-3xl shadow-2xl shadow-black/40 mt-10 max-w-2xl w-full border border-amber-200/60">
            {/* Level + Search — unified control block */}
            <div className="rounded-xl overflow-hidden border border-slate-200 mb-4">
              <div role="radiogroup" aria-label="Level of government">
                <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-[#162847] text-amber-400">
                  Level
                </div>
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  {(['federal', 'state'] as const).map(lvl => {
                    const isActive = govLevels.has(lvl);
                    return (
                      <button
                        key={lvl}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => {
                          setGovLevels(new Set([lvl]));
                          if (typeof window.umami !== 'undefined')
                            window.umami.track('level_checkbox_changed', { level: lvl, checked: true });
                        }}
                        className={`flex-1 py-2 rounded-lg transition-all cursor-pointer ${
                          isActive
                            ? lvl === 'federal'
                              ? 'bg-red-700 text-white shadow-sm'
                              : 'bg-amber-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <span className="block">{lvl === 'federal' ? 'Federal' : 'State'}</span>
                        <span className={`block text-xs font-normal ${isActive ? 'opacity-80' : 'opacity-50'}`}>
                          {lvl === 'federal' ? 'Congress & Senate' : 'Legislature & Governor'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="border-t border-slate-200" />
              <div role="radiogroup" aria-label="Search method">
                <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-[#162847] text-amber-400">
                  Find By
                </div>
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'address'}
                    onClick={() => { setSearchMode('address'); window.history.replaceState(null, '', '?mode=address#search'); }}
                    className={`flex-1 py-2 rounded-lg transition-all ${searchMode === 'address' ? 'bg-blue-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Address
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'name'}
                    onClick={() => { setSearchMode('name'); window.history.replaceState(null, '', '?mode=name#search'); }}
                    className={`flex-1 py-2 rounded-lg transition-all ${searchMode === 'name' ? 'bg-violet-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Name
                  </button>
                </div>
              </div>
            </div>

            {searchError && (
              <div role="alert" className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium animate-in fade-in duration-300">
                {searchError}
              </div>
            )}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {searchMode === 'address' ? (
                <div className="flex-1 flex flex-col gap-1">
                  {abroad && (
                    <p className="text-xs text-amber-600 font-medium px-1">
                      Your last U.S. voting address — this is your constituent identity and will appear as the return address on your postcard.
                    </p>
                  )}
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={abroad ? 'Your last U.S. voting address...' : 'Your street address...'}
                    required
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-slate-100 focus:border-slate-400 outline-none text-lg transition-all text-slate-900"
                  />
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 flex-1">
                  {govLevels.has('state') && (
                    <select
                      value={stateJurisdiction}
                      onChange={e => setStateJurisdiction(e.target.value)}
                      required={govLevels.has('state')}
                      className="px-4 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-slate-100 focus:border-slate-400 outline-none text-base text-slate-900 bg-white"
                    >
                      <option value="">Select state…</option>
                      {US_STATES.map(([abbr, label]) => (
                        <option key={abbr} value={abbr}>{label}</option>
                      ))}
                    </select>
                  )}
                  <input
                    type="text"
                    value={repName}
                    onChange={(e) => setRepName(e.target.value)}
                    placeholder="e.g. Bernard Sanders, Alexandria Ocasio-Cortez..."
                    required
                    className="flex-1 px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-slate-100 focus:border-slate-400 outline-none text-lg transition-all text-slate-900"
                  />
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-4 bg-[#07111e] hover:bg-[#162236] border border-amber-500/30 disabled:bg-slate-300 disabled:text-slate-500 text-white font-bold rounded-xl text-lg transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                {loading ? 'Searching...' : 'Find My Reps'}
              </button>
            </form>
            <p className="text-xs text-slate-500 mt-4 text-center">
              By using this site, you agree to our <Link href="/terms" className="underline hover:text-slate-400">Terms of Service</Link>.
            </p>
            {!showHowItWorks && (
              <div className="flex gap-1 mt-3 bg-slate-100 rounded-xl p-1 text-sm font-semibold">
                <Link href="/vs" className="flex-1 py-2 rounded-lg text-center text-slate-500 hover:text-slate-700 hover:bg-white/60 transition-all">
                  ⚔️ Compare two campaigns side by side →
                </Link>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-lg w-full text-center">
            {[
              { icon: '✍️', label: 'Write your message' },
              { icon: '🖨️', label: 'We print & mail it' },
              { icon: '📬', label: 'Delivered to Congress' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <div className="text-2xl">{icon}</div>
                <p className="text-xs text-slate-400 font-medium">{label}</p>
              </div>
            ))}
          </div>

          {!abroad && (
            <p className="text-sm text-slate-400 font-medium">
              ${getFormattedTotal(1)} for one card · 3 for ${getFormattedTotal(3)} · 8 for ${getFormattedTotal(8)} — printing &amp; postage included
            </p>
          )}

          {/* --- THE ANIMATED EXAMPLE POSTCARD --- */}
          <div className={`${showHowItWorks ? 'mt-8' : 'hidden sm:block mt-16'} w-full max-w-lg aspect-[6/4] bg-[#fdfbf7] rounded-sm shadow-2xl shadow-black/50 border border-slate-200 p-6 relative overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-500`}
               style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}>
            <div className="absolute left-[58%] top-[10%] bottom-[10%] w-px bg-amber-300/50"></div>
            <div className="absolute top-6 right-6 w-16 h-20 border-2 border-amber-300/60 flex items-center justify-center text-xs text-slate-400 font-bold tracking-tighter text-center bg-white/50 p-1">
              POSTAGE REQUIRED
            </div>
            <div className="flex h-full">
              <div className="w-[55%] pr-6 pt-4 text-left flex flex-col">
                <div className="font-sans text-slate-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap flex-grow">
                  <p className="font-bold mb-2">Dear Representative(s),</p>
                  <div className="mb-2 min-h-[80px]">
                    {displayedExampleText}
                    <span className="inline-block w-[2px] h-[1em] bg-slate-400 ml-1 animate-pulse align-middle"></span>
                  </div>
                  <p className="mt-2">
                    Sincerely,<br/>
                    <span className="italic text-slate-500 font-medium">[Constituent Name]</span>
                  </p>
                </div>
              </div>
              <div className="w-[45%] pl-8 flex items-center text-left">
                <div className="text-slate-800 font-mono text-xs uppercase leading-loose w-full blur-[1px] opacity-70">
                  <p className="font-bold text-slate-900 border-b border-slate-300 pb-2 mb-2">U.S. Representative</p>
                  <p>House of Representatives</p>
                  <p>Washington, DC 20515</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= STEP 2: SELECT (cart builder) ================= */}
      {step === 'select' && (
        <div className="max-w-5xl w-full mt-8 sm:mt-10 animate-in fade-in slide-in-from-bottom-8 duration-500 z-10 px-4">

          {searchError && (
            <div role="alert" className="mb-4 px-4 py-3 bg-red-900/30 border border-red-500/40 rounded-xl text-red-200 text-sm font-medium animate-in fade-in duration-300">
              {searchError}
            </div>
          )}
          {searchNotice && (
            <div className="mb-4 px-4 py-3 bg-amber-900/30 border border-amber-500/40 rounded-xl text-amber-200 text-sm font-medium animate-in fade-in duration-300">
              {searchNotice}
            </div>
          )}

          {/* ── Search Again bar ── */}
          <div className="bg-[#fafaf7] rounded-2xl border border-amber-200/60 shadow-lg p-4 mb-8">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Search for more representatives</p>
            <div className="rounded-xl overflow-hidden border border-slate-200 mb-3">
              <div role="radiogroup" aria-label="Level of government">
                <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-[#162847] text-amber-400">
                  Level
                </div>
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  {(['federal', 'state'] as const).map(lvl => {
                    const isActive = govLevels.has(lvl);
                    return (
                      <button
                        key={lvl}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => {
                          setGovLevels(new Set([lvl]));
                          if (typeof window.umami !== 'undefined')
                            window.umami.track('level_checkbox_changed', { level: lvl, checked: true });
                        }}
                        className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer ${
                          isActive
                            ? lvl === 'federal'
                              ? 'bg-red-700 text-white shadow-sm'
                              : 'bg-amber-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <span className="block">{lvl === 'federal' ? 'Federal' : 'State'}</span>
                        <span className={`block text-xs font-normal ${isActive ? 'opacity-80' : 'opacity-50'}`}>
                          {lvl === 'federal' ? 'Congress & Senate' : 'Legislature & Governor'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="border-t border-slate-200" />
              <div role="radiogroup" aria-label="Search method">
                <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-[#162847] text-amber-400">
                  Find By
                </div>
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'address'}
                    onClick={() => { setSearchMode('address'); window.history.replaceState(null, '', `?mode=address&levels=${Array.from(govLevels).join(',')}#select`); }}
                    className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'address' ? 'bg-blue-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Address
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'name'}
                    onClick={() => { setSearchMode('name'); window.history.replaceState(null, '', `?mode=name&levels=${Array.from(govLevels).join(',')}#select`); }}
                    className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'name' ? 'bg-violet-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Name
                  </button>
                </div>
              </div>
            </div>
            <form onSubmit={handleSearchAgain} className="flex gap-3">
              {searchMode === 'name' && govLevels.has('state') && (
                <select
                  value={stateJurisdiction}
                  onChange={e => setStateJurisdiction(e.target.value)}
                  required
                  className="px-3 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-slate-100 outline-none text-sm text-slate-900 bg-white"
                >
                  <option value="">State…</option>
                  {US_STATES.map(([abbr, label]) => (
                    <option key={abbr} value={abbr}>{label}</option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={searchMode === 'name' ? repName : address}
                onChange={(e) => searchMode === 'name' ? setRepName(e.target.value) : setAddress(e.target.value)}
                placeholder={searchMode === 'name' ? 'Search by name...' : 'Search by address...'}
                required
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-slate-100 focus:border-slate-400 outline-none text-base text-slate-900"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-[#07111e] hover:bg-[#162236] border border-amber-500/30 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all active:scale-95"
              >
                {loading ? '...' : 'Search'}
              </button>
            </form>
          </div>

          {/* ── Search results zone ── */}
          {searchResults.length > 0 && (() => {
            const federalResults = searchResults.filter((r: any) => r?.level !== 'state');
            const stateResults = searchResults.filter((r: any) => r?.level === 'state');
            const showSections = federalResults.length > 0 && stateResults.length > 0;
            const RepCard = ({ rep, index, prefix }: { rep: any; index: number; prefix: string }) => {
              const id = getRepId(rep) || `${prefix}-${index}`;
              const inCart = cartIds.includes(id);
              const fullName = getRepFullName(rep);
              const stateAbbr = getRepState(rep);
              const roleLabel = getRepTitle(rep);
              const role = stateAbbr ? `${roleLabel} · ${stateAbbr}` : roleLabel;
              const party = getRepParty(rep);
              const partyColor = party === 'Democrat' || party === 'Democratic' ? 'text-blue-600' : party === 'Republican' ? 'text-red-600' : 'text-slate-500';
              const repLink = rep?.level === 'state'
                ? null
                : `/rep/${repSlug(rep?.bio?.first_name || '', rep?.bio?.last_name || '')}`;
              return (
                <div key={id} className="relative p-5 rounded-2xl border-2 bg-[#fafaf7] border-slate-200 shadow">
                  <div className="pr-6">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{role}</p>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">
                      {repLink
                        ? <Link href={repLink} target="_blank" className="hover:text-amber-700 transition-colors">{fullName}</Link>
                        : fullName}
                    </h3>
                    <p className={`text-sm font-medium mt-1 ${partyColor}`}>{party}</p>
                  </div>
                  <button
                    onClick={() => addToCart(rep)}
                    disabled={inCart}
                    className={`mt-3 w-full py-2 rounded-xl text-sm font-bold transition-all ${
                      inCart
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-amber-500 hover:bg-amber-600 text-white active:scale-95'
                    }`}
                  >
                    {inCart ? '✓ Added' : 'Add to List →'}
                  </button>
                </div>
              );
            };
            const renderGroup = (reps: any[], label: string, prefix: string) => (
              <div className="mb-6">
                {showSections && (
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">{label}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {reps.map((rep, i) => <RepCard key={getRepId(rep) || `${prefix}-${i}`} rep={rep} index={i} prefix={prefix} />)}
                </div>
              </div>
            );
            return (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Search Results</p>
                  {searchResults.some((r: any) => !cartIds.includes(getRepId(r))) && (
                    <button
                      onClick={() => addAllToCart(searchResults)}
                      className="text-xs font-bold text-amber-500 hover:text-amber-400 border border-amber-500/30 rounded-lg px-3 py-1.5 transition-colors"
                    >
                      Add All →
                    </button>
                  )}
                </div>
                {federalResults.length > 0 && renderGroup(federalResults, 'Federal Representatives', 'fed')}
                {stateResults.length > 0 && renderGroup(stateResults, 'State Legislators', 'state')}
              </div>
            );
          })()}

          {/* ── Cart / Your List zone ── */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                Your List <span className="text-white">({cart.length})</span>
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-900/40 border border-amber-500/30 rounded-full text-amber-200 text-xs font-bold">
                🏷️ ${(getPerCardAverage(cart.length) / 100).toFixed(2)}/card
                {cart.length >= 3 && <span className="text-green-400">· Discount!</span>}
              </div>
            </div>

            {cart.length >= 1 && cart.length < 8 && hasUnadded && (
              <div className="mb-4 px-4 py-3 bg-blue-900/20 border border-blue-500/30 rounded-xl text-blue-200 text-sm">
                Add 1 more for just <strong>${(getMarginalCost(cart.length) / 100).toFixed(2)}</strong>
                {' '}→ {cart.length + 1} cards for <strong>${(getTotalPrice(cart.length + 1) / 100).toFixed(2)}</strong>
              </div>
            )}
            {cart.length === 8 && (
              <div className="mb-4 px-4 py-3 bg-green-900/20 border border-green-500/30 rounded-xl text-green-300 text-sm font-semibold">
                Maximum volume discount unlocked — $1.65 per additional card.
              </div>
            )}

            {cart.length === 0 ? (
              <div className="text-center py-12 text-slate-500 border-2 border-dashed border-slate-700 rounded-2xl">
                <p className="text-lg font-medium">No representatives added yet.</p>
                <p className="text-sm mt-1">Search above and click &quot;Add to List →&quot;</p>
              </div>
            ) : (
              <>
                {cart.length > 10 && (
                  <div className="mb-4 px-4 py-3 bg-amber-900/30 border border-amber-500/40 rounded-xl text-amber-300 text-sm font-medium">
                    That&apos;s {cart.length} postcards — total: <strong>${getFormattedTotal(cart.length)}</strong>. Make sure this is intentional!
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cart.map((rep: any, index: number) => {
                    const id = getRepId(rep) || `c-${index}`;
                    const fullName = getRepFullName(rep);
                    const stateAbbr = getRepState(rep);
                    const roleLabel = getRepTitle(rep);
                    const role = stateAbbr ? `${roleLabel} · ${stateAbbr}` : roleLabel;
                    const party = getRepParty(rep);
                    const partyColor = party === 'Democrat' || party === 'Democratic' ? 'text-blue-600' : party === 'Republican' ? 'text-red-600' : 'text-slate-500';
                    const repLink = rep?.level === 'state'
                      ? null
                      : `/rep/${repSlug(rep?.bio?.first_name || '', rep?.bio?.last_name || '')}`;
                    return (
                      <div key={id} className="relative p-5 rounded-2xl border-2 border-amber-500 bg-white shadow-xl shadow-amber-900/20">
                        <button
                          onClick={() => removeFromCart(id)}
                          className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 text-xs font-bold flex items-center justify-center transition-colors"
                          title="Remove"
                        >
                          ✕
                        </button>
                        <div className="pr-6">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{role}</p>
                          <h3 className="text-lg font-bold text-slate-900 leading-tight">
                            {repLink
                              ? <Link href={repLink} target="_blank" className="hover:text-amber-700 transition-colors">{fullName}</Link>
                              : fullName}
                          </h3>
                          <p className={`text-sm font-medium mt-1 ${partyColor}`}>{party}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* ── Bottom actions ── */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pb-8">
            <button onClick={() => window.history.back()} className="px-6 py-4 text-slate-300 hover:text-white rounded-xl transition-colors font-semibold w-full sm:w-auto">
              ← Start Over
            </button>

            <div className="relative w-full sm:w-auto">
              {cart.length >= 3 && (
                <div className="absolute -top-3 -right-3 bg-green-500 text-white text-[10px] uppercase tracking-wider font-extrabold px-3 py-1 rounded-full shadow-lg transform rotate-6 z-20 animate-in zoom-in duration-300">
                  Discount Applied!
                </div>
              )}
              <button
                onClick={() => goToStep('write')}
                disabled={cart.length === 0}
                className="px-10 py-4 bg-red-700 hover:bg-red-800 disabled:bg-slate-600 disabled:text-slate-400 text-white font-bold rounded-xl text-lg sm:text-xl transition-all shadow-lg hover:shadow-red-800/40 active:scale-95 w-full"
              >
                Write Message ({cart.length} {cart.length === 1 ? 'rep' : 'reps'}) · ${(cartTotal(cart.length) / 100).toFixed(2)} →
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-500 text-center pb-8">
            By using this site, you agree to our <Link href="/terms" className="underline hover:text-slate-400">Terms of Service</Link>.
          </p>
        </div>
      )}

      {/* ================= STEP 3: WRITE ================= */}
      {step === 'write' && (
        <div className="max-w-6xl w-full mt-4 sm:mt-6 animate-in fade-in slide-in-from-bottom-8 duration-500 px-4 z-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white">Write Your Message</h2>
            <p className="text-lg text-slate-300 mt-2">
              Keep it concise and respectful. We&apos;ll print it on a 4×6 postcard.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">

            {/* LEFT SIDE: LETTER EDITOR */}
            <div className="bg-[#fafaf7] p-5 sm:p-8 rounded-3xl shadow-2xl shadow-black/40 border border-amber-200/50 flex flex-col h-full">
              <label htmlFor="message" className="block text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">
                Message to Congress
              </label>

              <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-5 focus-within:border-slate-900 focus-within:ring-1 focus-within:ring-slate-900 transition-all flex flex-col">
                <p className="text-slate-800 font-serif text-lg mb-2">Dear Representative(s),</p>
                <textarea
                  id="message"
                  rows={6}
                  className="w-full bg-transparent border-none p-0 focus:ring-0 outline-none resize-none text-slate-900 text-lg font-serif leading-relaxed"
                  placeholder="Type the core of your message here. Keep it concise and impactful..."
                  value={message}
                  onChange={handleMessageChange}
                ></textarea>
                <p className="text-slate-800 font-serif text-lg mt-2">
                  Sincerely,<br/>
                  <span className="italic text-slate-500">[Your Name - Added at Checkout]</span>
                </p>
              </div>

              <div className="flex justify-between mt-3 text-sm font-medium">
                <p className="text-slate-400">Be clear and direct.</p>
                <p className={message.length > MAX_CHARS * 0.9 ? "text-red-600 font-bold" : "text-slate-400"}>
                  {message.length} / {MAX_CHARS}
                </p>
              </div>

              {/* Inspiration chips — shown only while the message is empty */}
              {message.trim().length === 0 && (
                <div className="mt-3 animate-in fade-in duration-300">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">💡 Need inspiration? Tap an example to start from it:</p>
                  <div className="flex flex-col gap-2">
                    {exampleMessages.map((ex, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setMessage(ex.slice(0, MAX_CHARS));
                          window.umami?.track('example_message_used', { index: i });
                        }}
                        className="text-left text-sm text-slate-600 bg-white border border-slate-200 hover:border-amber-400 hover:bg-amber-50 rounded-xl px-4 py-2.5 transition-colors leading-snug"
                      >
                        {ex.length > 110 ? `${ex.slice(0, 110)}…` : ex}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Campaign match */}
              {matchedCampaign && (
                <div className="mt-3 mb-3 flex flex-col gap-2 animate-in fade-in duration-300">
                  <p className="text-sm text-slate-600 font-medium">
                    🔥 This message has already been sent to Congress{' '}
                    <span className="text-red-600 font-bold">{matchedCampaign.send_count} times</span>.
                    {' '}Share the campaign link:
                  </p>
                  <div className="flex items-center gap-2 bg-[#07111e] border border-amber-500/20 rounded-xl px-4 py-3">
                    <span className="text-amber-400 text-sm shrink-0">🔗</span>
                    <span className="text-slate-400 text-xs truncate flex-1 font-mono">
                      postcardstocongress.org/m/{matchedCampaign.slug || matchedCampaign.id}
                    </span>
                    <button
                      type="button"
                      onClick={handleCampaignCopy}
                      className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg transition-all bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20"
                    >
                      {campaignCopied ? '✓ Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              )}

              {/* Abroad: voting address form */}
              {abroad && (
                <div className="mt-5 mb-1 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-0.5">📮 Return address on your postcard</p>
                  <p className="text-xs text-amber-700/60 mb-3">Your last U.S. voting address — will appear as the return address on the card.</p>
                  <div className="flex flex-col gap-2">
                    <input
                      type="text"
                      value={votingAddressInput.line1}
                      onChange={e => setVotingAddressInput(v => ({ ...v, line1: e.target.value }))}
                      placeholder="Street address"
                      className="w-full px-4 py-2.5 rounded-lg border border-amber-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={votingAddressInput.city}
                        onChange={e => setVotingAddressInput(v => ({ ...v, city: e.target.value }))}
                        placeholder="City"
                        className="flex-1 px-4 py-2.5 rounded-lg border border-amber-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
                      />
                      <input
                        type="text"
                        value={votingAddressInput.state}
                        onChange={e => setVotingAddressInput(v => ({ ...v, state: e.target.value }))}
                        placeholder="State"
                        maxLength={2}
                        className="w-20 px-4 py-2.5 rounded-lg border border-amber-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none uppercase"
                      />
                      <input
                        type="text"
                        value={votingAddressInput.zip}
                        onChange={e => setVotingAddressInput(v => ({ ...v, zip: e.target.value }))}
                        placeholder="ZIP"
                        maxLength={10}
                        className="w-24 px-4 py-2.5 rounded-lg border border-amber-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Promo code */}
              <div className="mt-4 mb-3">
                {!showPromoInput ? (
                  <button
                    type="button"
                    onClick={() => setShowPromoInput(true)}
                    className="w-full py-3 rounded-xl border border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-600 text-sm font-medium transition-colors"
                  >
                    🏷️ Have a promo code?
                  </button>
                ) : (
                  <div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => { setPromoCode(e.target.value); setAppliedPromo(null); setPromoError(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                        placeholder="Enter promo code"
                        autoFocus
                        className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-400 outline-none text-sm text-slate-900"
                      />
                      <button
                        type="button"
                        onClick={handleApplyPromo}
                        disabled={promoLoading || !promoCode.trim()}
                        className="px-5 py-3 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap"
                      >
                        {promoLoading ? '...' : 'Apply'}
                      </button>
                    </div>
                    {appliedPromo && (
                      <p className="mt-2 text-sm text-green-600 font-semibold">✓ {appliedPromo.label} applied!</p>
                    )}
                    {promoError && (
                      <p className="mt-2 text-sm text-red-500 font-medium">✗ {promoError}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 flex flex-col gap-2 text-sm text-slate-600">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={okToContact} onChange={e => setOkToContact(e.target.checked)} className="w-4 h-4 rounded accent-red-700" />
                  It&apos;s okay for us to contact you via email.
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={subscribeNewsletter} onChange={e => setSubscribeNewsletter(e.target.checked)} className="w-4 h-4 rounded accent-red-700" />
                  Subscribe to our newsletter.
                </label>
              </div>
              <div className="mb-4" />

              {checkoutError && (
                <div role="alert" className="mb-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium animate-in fade-in duration-300">
                  {checkoutError}
                </div>
              )}

              <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:gap-4 mt-auto">
                <button onClick={() => window.history.back()} className="px-6 py-4 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors font-bold text-center">
                  ← Back
                </button>
                <button
                  onClick={handleCheckout}
                  disabled={message.length < 10 || isCheckingOut}
                  className="flex-1 px-6 py-4 bg-red-700 hover:bg-red-800 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold rounded-xl text-lg sm:text-xl transition-all shadow-lg hover:shadow-red-800/30 active:scale-95"
                >
                  {isCheckingOut ? 'Loading Secure Checkout...' : `Checkout - $${(cartTotal(cart.length) / 100).toFixed(2)}`}
                </button>
              </div>
              {message.length < 10 && (
                <p className="text-xs text-amber-700 mt-2 text-center font-medium">
                  Write at least 10 characters to continue to checkout.
                </p>
              )}
              <p className="text-xs text-slate-500 mt-4 text-center">
                By using this site, you agree to our <Link href="/terms" className="underline hover:text-slate-400">Terms of Service</Link>.
                {abroad
                  ? ' Your U.S. voting address above will appear as the return address on the postcard.'
                  : ' Your billing address on file with your payment method will be used as the return address on the postcard — please ensure it is current.'
                }
              </p>
            </div>

            {/* RIGHT SIDE: LIVE PREVIEW */}
            <div className="sticky top-6 lg:top-10 flex flex-col">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center lg:text-left">Live Preview (Back of Card)</h3>

              <div className="aspect-[6/4] bg-[#fdfbf7] rounded-sm shadow-2xl shadow-black/50 border border-slate-200 p-4 sm:p-6 relative overflow-hidden"
                   style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}>
                <div className="absolute left-[58%] top-[10%] bottom-[10%] w-px bg-amber-300/50"></div>
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 w-14 h-16 sm:w-16 sm:h-20 border-2 border-amber-300/60 flex items-center justify-center text-[10px] sm:text-xs text-slate-400 font-bold tracking-tighter text-center bg-white/50 p-1">
                  POSTAGE REQUIRED
                </div>
                <div className="flex h-full">
                  <div className="w-[55%] pr-4 sm:pr-6 pt-2 sm:pt-4 flex flex-col">
                    <div className="font-sans text-slate-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap flex flex-col h-full">
                      <p className="font-bold mb-2">Dear Representative(s),</p>
                      <div className="flex-grow break-words">
                        {message || <span className="text-slate-300 italic">Your message will appear here...</span>}
                      </div>
                      <p className="mt-2">
                        Sincerely,<br/>
                        <span className="italic text-slate-500 font-medium">[Your Name]</span>
                      </p>
                    </div>
                  </div>
                  <div className="w-[45%] pl-4 sm:pl-8 flex items-center">
                    <div className="text-slate-800 font-mono text-[10px] sm:text-xs md:text-sm uppercase leading-loose w-full">
                      <p className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-2">{previewRepName}</p>
                      {previewAddressLines.map((line, i) => line && <p key={i}>{line}</p>)}
                      <p className="mt-6 text-[9px] sm:text-[10px] text-slate-500 normal-case leading-tight">
                        From: Constituent<br/>
                        {address
                          ? (address.length > 35 ? `${address.substring(0, 35)}…` : address)
                          : '[Your billing address]'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {(() => {
                const campaignUrl = matchedCampaign
                  ? `https://postcardstocongress.org/m/${matchedCampaign.slug || matchedCampaign.id}`
                  : null;
                return (
                  <ShareButtons
                    url={campaignUrl ?? 'https://postcardstocongress.org'}
                    text={
                      matchedCampaign
                        ? `This message has already been sent to Congress ${matchedCampaign.send_count} times. Add your voice:`
                        : 'Send a postcard to Congress in under a minute:'
                    }
                    title={
                      matchedCampaign
                        ? `🔥 ${matchedCampaign.send_count} people have sent this message.`
                        : 'Your voice matters. Spread the word.'
                    }
                    subtitle={
                      matchedCampaign
                        ? 'Join this campaign — share the link and add more voices:'
                        : 'Share PostcardsToCongress.org and help others find their reps:'
                    }
                    footer={
                      matchedCampaign
                        ? null
                        : "Once your postcard is sent, you'll receive a unique shareable link for your campaign."
                    }
                    showCard
                    className="mt-auto pt-4"
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
